import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Switch } from '../ui/switch';
import { BarChart3, Table as TableIcon } from 'lucide-react';
import { LoadingSpinner } from '../events/LoadingSpinner';
import { DailyMusterCount } from '../../services/musterCountService';

interface MusterCountTableProps {
    data: DailyMusterCount[];
    viewMode: 'graph' | 'table';
    onViewModeChange: (mode: 'graph' | 'table') => void;
    isLoading?: boolean;
}

export const MusterCountTable: React.FC<MusterCountTableProps> = ({ data, viewMode, onViewModeChange, isLoading = false }) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-xl sm:text-2xl">Daily Muster Count Data</CardTitle>
                                <CardDescription className="text-xs sm:text-sm">Muster roll count and manpower generated details</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <BarChart3 className={`w-4 h-4 ${viewMode === 'graph' ? 'text-brand' : 'text-gray-400'}`} />
                                <Switch
                                    checked={viewMode === 'table'}
                                    onChange={(e) => onViewModeChange(e.target.checked ? 'table' : 'graph')}
                                />
                                <TableIcon className={`w-4 h-4 ${viewMode === 'table' ? 'text-brand' : 'text-gray-400'}`} />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-center py-20">
                            <LoadingSpinner />
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        );
    }

    if (data.length === 0) {
        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-xl sm:text-2xl">Daily Muster Count Data</CardTitle>
                                <CardDescription className="text-xs sm:text-sm">Muster roll count and manpower generated details</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <BarChart3 className={`w-4 h-4 ${viewMode === 'graph' ? 'text-brand' : 'text-gray-400'}`} />
                                <Switch
                                    checked={viewMode === 'table'}
                                    onChange={(e) => onViewModeChange(e.target.checked ? 'table' : 'graph')}
                                />
                                <TableIcon className={`w-4 h-4 ${viewMode === 'table' ? 'text-brand' : 'text-gray-400'}`} />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="h-96 flex items-center justify-center text-gray-500">
                            <p>No data available. Please apply filters to view the table.</p>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <CardTitle className="text-xl sm:text-2xl">Daily Muster Count Data</CardTitle>
                            <CardDescription className="text-xs sm:text-sm">Muster roll count and manpower generated details</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <BarChart3 className={`w-4 h-4 ${viewMode === 'graph' ? 'text-brand' : 'text-gray-400'}`} />
                            <Switch
                                checked={viewMode === 'table'}
                                onChange={(e) => onViewModeChange(e.target.checked ? 'table' : 'graph')}
                            />
                            <TableIcon className={`w-4 h-4 ${viewMode === 'table' ? 'text-brand' : 'text-gray-400'}`} />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border overflow-hidden">
                        <div className="overflow-x-auto">
                            <Table className="min-w-[640px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[80px] sticky left-0 bg-white z-10">S. No</TableHead>
                                        <TableHead className="min-w-[120px]">Date</TableHead>
                                        <TableHead className="text-center min-w-[140px]">Muster Count</TableHead>
                                        {/* <TableHead className="text-center min-w-[160px]">Manpower generated</TableHead> */}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.map((item, index) => (
                                        <TableRow key={item.date}>
                                            <TableCell className="font-medium sticky left-0 bg-white z-10">{index + 1}</TableCell>
                                            <TableCell className="whitespace-nowrap">{formatDate(item.date)}</TableCell>
                                            <TableCell className="text-center whitespace-nowrap">{item.musterRollCount.toLocaleString()}</TableCell>
                                            {/* <TableCell className="text-center whitespace-nowrap">{item.attendanceCount.toLocaleString()}</TableCell> */}
                                        </TableRow>
                                    ))}
                                    {/* Total Row */}
                                    <TableRow className="bg-brand-light font-semibold">
                                        <TableCell className="font-semibold sticky left-0 bg-brand-light z-10">Total</TableCell>
                                        <TableCell className="whitespace-nowrap font-semibold">-</TableCell>
                                        <TableCell className="text-center whitespace-nowrap font-semibold">
                                            {data.reduce((sum, item) => sum + item.musterRollCount, 0).toLocaleString()}
                                        </TableCell>
                                        {/* <TableCell className="text-center whitespace-nowrap font-semibold">
                                            {data.reduce((sum, item) => sum + item.attendanceCount, 0).toLocaleString()}
                                        </TableCell> */}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

